import { Ionicons } from '@expo/vector-icons';
import { bookingFeeUsd, withCardFee } from '@suitescanner/pricing';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Image, type ImageSourcePropType, Pressable, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { Image as ExpoImage } from 'expo-image';

import { api } from '~/api';
import { errorMessage } from '~/api/client';
import { track } from '~/lib/analytics';
import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { PayUModal } from '~/components/PayUModal';
import { openPayUPopup, PAYU_POPUP_NAME, submitPayUForm } from '~/lib/payuPopup';
import { Button, Select } from '~/components/ui';
import { ModalSheet } from '~/components/ui/ModalSheet';
import { getAmenityIcon } from '~/lib/amenityIcons';
import { formatUtcDateTime, parseSupplierDateTime } from '~/lib/dateUtils';
import { guestNameProblem, normalizeGuestName } from '~/lib/guestNames';
import { openFreeCancellationBefore } from '~/lib/cancellation';
import { stashCheckout } from '~/lib/checkoutHandoff';
import { decodeOccupancy, type Occupancy } from '~/lib/occupancy';
import { DEFAULT_PHONE_COUNTRY, normalizeResidency, RESIDENCY_OPTIONS } from '~/lib/residency';
import { useMember } from '~/stores/useMember';
import { useCurrency } from '~/stores/useCurrency';
import { convertPriceCeil, detectUserCountry, formatCurrency, formatExactAmount } from '~/lib/currency';
import { extractThumbnailUrl, resolveThumbnailSource } from '~/lib/images';
import { TaxAmount } from '~/components/TaxAmount';
import { formatTaxName } from '~/lib/taxes';
import { DIAL_CODES, PHONE_COUNTRY_OPTIONS, dialCodeFor, formatPhoneDigits, parsePhoneDigits } from '~/lib/dialCodes';
import { loadTravellerProfile } from '~/lib/travellerProfile';
import { colors, control, fontFamily, maxContentWidth, price, radii, space, spacing, type } from '~/ui/tokens';
import { CountryFlag } from '~/components/ui/CountryFlag';

type PrebookData = Api.Booking.Prebook.Response['data'];
type CheckoutParams = { bookHash: string; hotelId: string; checkin: string; checkout: string; occ: string; residency?: string; hotelName?: string; hotelAddress?: string; hotelStars?: string; hotelImage?: string; reviewScore?: string; reviewCount?: string; hotelAmenities?: string; mealIncluded?: string; paymentType?: string; freeCancellationBefore?: string; checkInTime?: string; checkOutTime?: string; roomName?: string; roomPrice?: string; propertyTaxes?: string };

function createFallbackPrebook(params: CheckoutParams): PrebookData {
    const priceAmount = params.roomPrice || '229.00';
    const roomName = params.roomName || 'Selected room';
    const hasBreakfast = params.mealIncluded === 'true';
    const freeCancel = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    return {
        status: 'ok',
        error: null,
        bookHash: params.bookHash || 'h-amari-premier',
        priceChanged: false,
        rateQuote: null,
        rate: {
            book_hash: params.bookHash || 'h-amari-premier',
            match_hash: `m_${params.bookHash}`,
            daily_prices: [priceAmount],
            room_name: roomName,
            meal_data: {
                value: hasBreakfast ? 'breakfast' : 'nomeal',
                has_breakfast: hasBreakfast
            },
            payment_options: {
                payment_types: [
                    {
                        type: params.paymentType || 'deposit',
                        amount: priceAmount,
                        show_amount: priceAmount,
                        currency_code: 'USD',
                        show_currency_code: 'USD',
                        is_need_credit_card_data: true,
                        tax_data: { taxes: [] },
                        cancellation_penalties: {
                            policies: [
                                { start_at: null, end_at: freeCancel, amount_charge: '0.00' },
                                { start_at: freeCancel, end_at: null, amount_charge: priceAmount }
                            ],
                            free_cancellation_before: freeCancel
                        }
                    }
                ]
            },
            room_data_trans: {
                main_room_type: roomName,
                bedding_type: '1 King Bed'
            }
        }
    } as unknown as PrebookData;
}

function roomOccupancyLabel(room: Occupancy[number]): string {
    const adults = `${room.adults} ${room.adults === 1 ? 'adult' : 'adults'}`;
    if (room.children.length === 0) return adults;
    const children = `${room.children.length} ${room.children.length === 1 ? 'child' : 'children'} (age ${room.children.join(', ')})`;
    return `${adults} · ${children}`;
}

// Compact form for the selected-room confirmation ("Guests: 2 adults, 1
// child") — no ages, comma-joined rather than roomOccupancyLabel's "·".
function guestsSummaryLabel(room: Occupancy[number]): string {
    const adults = `${room.adults} ${room.adults === 1 ? 'adult' : 'adults'}`;
    if (room.children.length === 0) return adults;
    return `${adults}, ${room.children.length} ${room.children.length === 1 ? 'child' : 'children'}`;
}

function stayLength(checkin?: string, checkout?: string): number {
    if (!checkin || !checkout) return 1;
    const difference = new Date(`${checkout}T00:00:00Z`).getTime() - new Date(`${checkin}T00:00:00Z`).getTime();
    return Math.max(1, Math.round(difference / 86_400_000));
}

type ShownTax = { name: string; amount: string; currencyCode: string };

function parseShownTaxes(value?: string): ShownTax[] | null {
    if (value === undefined) return null;
    try {
        const parsed = JSON.parse(value) as unknown;
        return Array.isArray(parsed) ? parsed.filter((tax): tax is ShownTax => Boolean(tax) && typeof tax === 'object' && typeof (tax as ShownTax).name === 'string') : null;
    } catch {
        return null;
    }
}

// Property-payable taxes that differ from what the hotel page showed, as
// "City tax US$17.90 (was US$17.70)". Empty when nothing changed or when the
// hotel page did not say what it showed (older links).
function changedPropertyTaxes(shown: ShownTax[] | null, quoted: Array<{ name: string; amount: string; currency_code: string }>): string[] {
    if (!shown) return [];
    const key = (name: string, currency: string) => `${name}|${currency.toUpperCase()}`;
    const before = new Map(shown.map(tax => [key(tax.name, tax.currencyCode), Number(tax.amount)]));
    const after = new Map(quoted.map(tax => [key(tax.name, tax.currency_code), Number(tax.amount)]));
    const changes: string[] = [];
    for (const tax of quoted) {
        const was = before.get(key(tax.name, tax.currency_code));
        const now = Number(tax.amount);
        if (was === undefined) changes.push(`${formatTaxName(tax.name)} ${formatExactAmount(now, tax.currency_code)} (new)`);
        else if (Math.abs(was - now) >= 0.005) changes.push(`${formatTaxName(tax.name)} ${formatExactAmount(now, tax.currency_code)} (was ${formatExactAmount(was, tax.currency_code)})`);
    }
    for (const tax of shown) {
        if (!after.has(key(tax.name, tax.currencyCode))) changes.push(`${formatTaxName(tax.name)} no longer charged`);
    }
    return changes;
}

function sameCancellationDeadline(original?: string, confirmed?: string | null): boolean {
    const left = original || null;
    const right = confirmed || null;
    if (!left || !right) return left === right;
    const leftTime = parseSupplierDateTime(left).getTime();
    const rightTime = parseSupplierDateTime(right).getTime();
    return Number.isFinite(leftTime) && Number.isFinite(rightTime) ? leftTime === rightTime : left === right;
}

// ETG Price Rule: Total room rate must reflect whole-stay contract price, not
// single-night daily rate. Shared by the render body and the prebook
// telemetry so the two never disagree on what "the total" is.
function roomTotalFrom(rate: PrebookData['rate'] | null | undefined, nights: number): number {
    const paymentAmount = Number(rate?.payment_options?.payment_types?.[0]?.amount);
    const dailyPrices = rate?.daily_prices?.map(Number).filter(Number.isFinite) ?? [];
    const dailySum = dailyPrices.reduce((sum, price) => sum + price, 0);
    return paymentAmount > 0 ? paymentAmount : (dailySum > 0 ? (dailyPrices.length === 1 && nights > 1 ? dailySum * nights : dailySum) : 0);
}

// What the guest pays: the supplier price plus the card fee, plus our booking
// fee. The booking fee tier is chosen on the supplier price, before the card fee.
function totalWithBookingFee(rate: PrebookData['rate'] | null | undefined, nights: number): number {
    const supplierTotal = roomTotalFrom(rate, nights);
    return withCardFee(supplierTotal) + bookingFeeUsd(supplierTotal);
}

function currencyOf(rate: PrebookData['rate'] | null | undefined): string {
    return rate?.payment_options?.payment_types?.[0]?.show_currency_code ?? 'USD';
}

// Total guest composition across every room ("2 adults" / "4 adults and 1 child") —
// the actual occupancy breakdown, not just a combined headcount.
function occupancySummaryLabel(occupancy: Occupancy): string {
    const totalAdults = occupancy.reduce((sum, room) => sum + room.adults, 0);
    const totalChildren = occupancy.reduce((sum, room) => sum + room.children.length, 0);
    const adultsLabel = `${totalAdults} ${totalAdults === 1 ? 'adult' : 'adults'}`;
    if (totalChildren === 0) return adultsLabel;
    return `${adultsLabel} and ${totalChildren} ${totalChildren === 1 ? 'child' : 'children'}`;
}

// Compact " · "-joined line for secondary room conditions, skipping anything absent.
function joinMeta(...parts: Array<string | null | undefined | false>): string {
    return parts.filter((part): part is string => Boolean(part)).join(' · ');
}

// Calendar-date formatting for a plain "Sep 21" style label. Explicit UTC
// keeps this reading the booking's actual date instead of drifting a day
// depending on the viewer's local timezone (west of UTC would otherwise show
// the day before).
function dateLabel(value?: string): string {
    if (!value) return 'Date to be confirmed';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
}

// Same date, with weekday and year, for the booking-details card where the
// date is the dominant element.
function dateLabelFull(value?: string): string {
    if (!value) return 'Date to be confirmed';
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
}

// Friendlier than the raw "Sep 19, 2026, 18:30 UTC": 12-hour clock plus
// weekday, still explicitly UTC-labelled so the deadline is never ambiguous
// (we don't have the property's own timezone in our data model).
function cancellationDeadlineLabel(iso: string): string {
    const date = parseSupplierDateTime(iso);
    const datePart = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
    const timePart = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' }).format(date);
    return `${datePart}, ${timePart} UTC`;
}

function money(value: number, fromCurrency = 'USD'): string {
    const { selectedCurrency, rates } = useCurrency.getState();
    const converted = convertPriceCeil(value, fromCurrency, selectedCurrency, rates);
    return formatCurrency(converted, selectedCurrency);
}

function imageSource(value?: string): ImageSourcePropType | null {
    return resolveThumbnailSource(value);
}

function parseAmenities(value?: string): string[] {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch { return []; }
}

function ratingLabel(score: number): string {
    if (score >= 9) return 'Superb';
    if (score >= 8) return 'Excellent';
    if (score >= 7) return 'Very good';
    return 'Good';
}

// The raw room_name often bundles bed/smoking/meal qualifiers into one string
// (e.g. "Junior Suite, 1 King Bed, Non-Smoking"). Room title = the part before
// that qualifier list, same convention the hotel page uses for roomGroupName.
function roomTitleFrom(rate: NonNullable<PrebookData['rate']>): string {
    return rate.room_data_trans?.main_room_type?.trim() || rate.room_name.split('(')[0].split(',')[0].trim() || 'Selected room';
}

// Smoking status is not a dedicated field in the ETG rate contract, but the
// supplier's own room_name sometimes states it explicitly (e.g. "..., Non-Smoking").
// Checked against the raw name only — never inferred from the room title alone.
function smokingStatus(rawRoomName: string): 'no_smoking' | 'smoking' | null {
    if (/non[-\s]?smoking/i.test(rawRoomName)) return 'no_smoking';
    if (/\bsmoking\b/i.test(rawRoomName)) return 'smoking';
    return null;
}

function CheckoutScreen() {
    const router = useRouter();
    const params = useLocalSearchParams<CheckoutParams>();
    const { width } = useWindowDimensions();
    const narrow = width < 960;
    // The phone row (country-select + digits) needs far less width than the
    // page's own single-column breakpoint to sit side by side — but the
    // 380px property column right next to it also squeezes it as soon as
    // that breakpoint is crossed, all the way until the page hits
    // maxContentWidth and the form column gets its full share back. Both
    // ranges are genuinely tight; the broad single-column middle in between
    // (still not phone-narrow) has width to spare.
    const phoneRowNarrow = width < 420 || (width >= 960 && width < maxContentWidth);
    const occupancy = decodeOccupancy(params.occ);
    const openMemberPrompt = useMember(state => state.openPrompt);
    const isMember = useMember(state => state.isMember);
    const memberEmail = useMember(state => state.user?.email ?? null);
    const memberName = useMember(state => state.user?.name ?? '');
    const isAdmin = useMember(state => state.user?.isAdmin === true);
    const [prebook, setPrebook] = useState<PrebookData | null>(null);
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false);
    const [prebookAttempt, setPrebookAttempt] = useState(0);
    const [guests, setGuests] = useState<Array<{ first: string; last: string }>>(() => occupancy.map(() => ({ first: '', last: '' })));
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState(() => {
        const detected = detectUserCountry();
        return DIAL_CODES[detected] ? detected : DEFAULT_PHONE_COUNTRY;
    });
    const [phoneFocused, setPhoneFocused] = useState(false);
    // The rate was fetched for this citizenship, chosen at search time. It is
    // fixed for checkout: changing it means a new search, never an edit here.
    const nationality = normalizeResidency(params.residency);

    useEffect(() => {
        if (!memberEmail) return;
        // Signed in: the confirmation email and the lead guest's name start filled
        // in (from the saved traveller profile, else the account name) and stay editable.
        setEmail(current => current || memberEmail);
        const saved = loadTravellerProfile(memberEmail);
        const nameParts = memberName.trim().split(/\s+/).filter(Boolean);
        const accountFirst = nameParts[0] ?? '';
        const accountLast = nameParts.slice(1).join(' ');
        setGuests(current => current.map((guest, index) => index === 0 ? { first: guest.first || saved?.firstName || accountFirst, last: guest.last || saved?.lastName || accountLast } : guest));
        if (!saved) return;
        setPhone(current => current || saved.phone);
        setPhoneCountry(current => phone ? current : saved.phoneCountry);
    }, [memberEmail, memberName]);
    const [specialRequest, setSpecialRequest] = useState('');
    const [specialRequestOpen, setSpecialRequestOpen] = useState(false);
    const [priceChangeAccepted, setPriceChangeAccepted] = useState(false);
    const [priceReviewOpen, setPriceReviewOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [payuModalVisible, setPayuModalVisible] = useState(false);
    const [payuSessionData, setPayuSessionData] = useState<Api.Payment.Initiate.Data | null>(null);
    const [payuPopupBlocked, setPayuPopupBlocked] = useState(false);
    const [payuInitiationError, setPayuInitiationError] = useState<string | null>(null);
    const [openingPayment, setOpeningPayment] = useState(false);
    const [redirectSeconds, setRedirectSeconds] = useState<number | null>(null);
    const payuPopupRef = useRef<Window | null>(null);
    const submittingRef = useRef(false);
    const checkoutStartedAtRef = useRef<number | null>(null);
    const screenMountedAtRef = useRef(Date.now());
    const lastStepRef = useRef<'guest_details' | 'prebook' | 'payment_redirect'>('prebook');
    const checkoutCompletedRef = useRef(false);
    const prebookRef = useRef<PrebookData | null>(null);

    useEffect(() => {
        prebookRef.current = prebook;
    }, [prebook]);

    // Announces both checkout.started (first successful confirmation of this
    // rate) and checkout.prebook_result (every prebook response, first call
    // or the stale-rate refresh inside submit()) from one place so the two
    // events always describe the same resolved data.
    function announcePrebook(nights: number, data: PrebookData, oldTotal: { amount: number; currency: string } | null) {
        const currency = currencyOf(data.rate);
        const newTotal = { amount: totalWithBookingFee(data.rate, nights), currency };
        if (!checkoutStartedAtRef.current && data.bookHash) {
            checkoutStartedAtRef.current = Date.now();
            lastStepRef.current = 'guest_details';
            track('checkout.started', { bookHash: data.bookHash, hid: Number(params.hotelId), total: newTotal });
        }
        if (data.bookHash) {
            track('checkout.prebook_result', { bookHash: data.bookHash, priceChanged: data.priceChanged, oldTotal, newTotal });
        }
    }

    useEffect(() => {
        setLoading(true);
        setUnavailable(false);
        setPrebook(null);
        setPriceChangeAccepted(false);
        let active = true;
        // Real hotelpage hashes also start with h-. Never manufacture a rate
        // when a live prebook fails; only the named preview fixtures may fall back.
        const isMockHash = !params.bookHash || params.bookHash.startsWith('mock_') ||
            (params.bookHash.startsWith('h-') && !/^h-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.bookHash));
        const nights = stayLength(params.checkin, params.checkout);

        api.booking.prebook({ hash: params.bookHash }).then(res => {
            if (!active) return;
            if (res.data?.status === 'ok' && res.data?.bookHash) {
                setPrebook(res.data);
                announcePrebook(nights, res.data, null);
            } else if (isMockHash) {
                const fallback = createFallbackPrebook(params);
                setPrebook(fallback);
                announcePrebook(nights, fallback, null);
            } else {
                setUnavailable(true);
            }
            setLoading(false);
        }).catch(() => {
            if (!active) return;
            if (isMockHash) {
                const fallback = createFallbackPrebook(params);
                setPrebook(fallback);
                announcePrebook(nights, fallback, null);
            } else {
                setUnavailable(true);
            }
            setLoading(false);
        });
        return () => { active = false; };
    }, [params.bookHash, prebookAttempt]);

    // pagehide only — not visibilitychange, which also fires on an ordinary
    // tab switch and would misreport most of those as abandonment.
    useEffect(() => {
        function handlePageHide() {
            const bookHash = prebookRef.current?.bookHash;
            if (checkoutCompletedRef.current || !bookHash) return;
            track('checkout.abandoned', {
                bookHash,
                lastStep: lastStepRef.current,
                secondsInCheckout: Math.round((Date.now() - screenMountedAtRef.current) / 1000),
            }, { immediate: true });
        }
        try {
            window.addEventListener('pagehide', handlePageHide);
        } catch {
            return undefined;
        }
        return () => {
            try {
                window.removeEventListener('pagehide', handlePageHide);
            } catch {
                // no DOM to remove the listener from
            }
        };
    }, []);

    const setGuest = (index: number, field: 'first' | 'last', value: string) => setGuests(current => current.map((guest, i) => (i === index ? { ...guest, [field]: value } : guest)));
    const parsedPhone = parsePhoneDigits(phone, phoneCountry);
    const fullPhone = parsedPhone.e164;
    const guestErrors = guests.map(guest => ({ first: guestNameProblem(guest.first), last: guestNameProblem(guest.last) }));
    // What still blocks payment, named under the button: on a phone the empty
    // field can be several screens above it.
    const missing = [
        guestErrors.some(errors => errors.first || errors.last) ? 'guest names in Latin letters' : null,
        email.includes('@') ? null : 'a valid email address',
        parsedPhone.possible ? null : 'a phone number',
        nationality ? null : 'nationality',
    ].filter((item): item is string => item !== null);
    const valid = missing.length === 0;

    const nights = stayLength(params.checkin, params.checkout);
    const supplierTotal = roomTotalFrom(prebook?.rate, nights);
    const roomTotal = withCardFee(supplierTotal);
    const bookingFee = isAdmin ? 0 : bookingFeeUsd(supplierTotal);
    const total = roomTotal + bookingFee;
    const guestsCount = occupancy.reduce((sum, room) => sum + room.adults + room.children.length, 0);
    const roomName = prebook?.rate?.room_name ?? params.roomName ?? 'Selected room';
    const roomTitle = prebook?.rate ? roomTitleFrom(prebook.rate) : params.roomName ?? 'Selected room';
    const payment = prebook?.rate?.payment_options?.payment_types?.[0];
    const hasBreakfast = prebook?.rate?.meal_data?.has_breakfast ?? (params.mealIncluded === 'true');
    const payAtProperty = (payment?.type ?? params.paymentType) === 'hotel';
    const bedType = prebook?.rate?.room_data_trans?.bedding_type?.trim() || null;
    const freeCancellationBefore = openFreeCancellationBefore(payment?.cancellation_penalties?.free_cancellation_before);
    const taxChanges = prebook ? changedPropertyTaxes(parseShownTaxes(params.propertyTaxes), (payment?.tax_data?.taxes ?? []).filter(tax => !tax.included_by_supplier)) : [];
    const termsChanged = Boolean(prebook) && (
        taxChanges.length > 0 ||
        (Boolean(params.paymentType) && Boolean(payment?.type) && params.paymentType !== payment?.type) ||
        (params.freeCancellationBefore !== undefined && !sameCancellationDeadline(params.freeCancellationBefore, freeCancellationBefore)) ||
        (params.mealIncluded !== undefined && typeof prebook?.rate?.meal_data?.has_breakfast === 'boolean' && (params.mealIncluded === 'true') !== hasBreakfast)
    );
    const requiresReview = Boolean(prebook?.priceChanged || termsChanged);
    const isFreeCancel = freeCancellationBefore !== null;
    const taxes = payment?.tax_data?.taxes ?? [];
    const nonIncludedTaxes = taxes.filter(tax => !tax.included_by_supplier);
    const smoking = smokingStatus(roomName);
    const reviewScore = Number(params.reviewScore || 0);
    const reviewCount = Number(params.reviewCount || 0);
    const hotelStars = Number(params.hotelStars) || 0;
    const hotelPhoto = imageSource(params.hotelImage);
    const hotelAmenities = parseAmenities(params.hotelAmenities);

    const nationalityName = RESIDENCY_OPTIONS.find(option => option.code === nationality)?.name ?? '';
    const canSubmit = valid && Boolean(prebook?.bookHash) && !loading && !unavailable && !submitting;

    function viewPaymentStatus(data: Api.Payment.Initiate.Data) {
        checkoutCompletedRef.current = true;
        setPayuModalVisible(false);
        router.replace({ pathname: '/payment-confirmed', params: { transactionId: data.transactionId, partnerOrderId: data.partnerOrderId } });
    }

    function openPaymentPage(data: Api.Payment.Initiate.Data) {
        if (!data.payu || typeof document === 'undefined') return;
        setRedirectSeconds(null);
        // A surviving PayU window should be focused, never submitted twice.
        if (payuPopupRef.current && !payuPopupRef.current.closed) {
            try { payuPopupRef.current.focus(); } catch { /* Browser may restrict focus. */ }
            return;
        }
        const popup = openPayUPopup();
        payuPopupRef.current = popup;
        setPayuPopupBlocked(!popup);
        setOpeningPayment(false);
        if (!popup) return;
        submitPayUForm(data.payu, PAYU_POPUP_NAME);
        try { popup.focus(); } catch { /* Browser may restrict focus. */ }
    }

    // The controlled status panel opens first. Browsers may block a window
    // opened by a timer, so its CTA provides a gesture-backed recovery path.
    useEffect(() => {
        if (!payuModalVisible || !payuSessionData?.payu || !openingPayment) return;
        setRedirectSeconds(3);
        const countdown = setInterval(() => setRedirectSeconds(value => value === null ? null : Math.max(1, value - 1)), 1000);
        const timer = setTimeout(() => openPaymentPage(payuSessionData), 3000);
        return () => { clearTimeout(timer); clearInterval(countdown); };
    }, [payuModalVisible, payuSessionData, openingPayment]);

    useEffect(() => {
        if (!payuSessionData || payuModalVisible || typeof window === 'undefined') return;
        let leftBookingTab = false;
        const onBlur = () => { leftBookingTab = true; };
        const onFocus = () => { if (leftBookingTab) setPayuModalVisible(true); };
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        return () => {
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
        };
    }, [payuSessionData, payuModalVisible]);

    function continueToPayment() {
        if (payuSessionData) {
            setPayuModalVisible(true);
            return;
        }
        if (requiresReview && !priceChangeAccepted) {
            setPriceReviewOpen(true);
            return;
        }
        void submit();
    }

    async function submit() {
        if (!prebook?.bookHash || !canSubmit || (requiresReview && !priceChangeAccepted) || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setError(null);
        setPayuInitiationError(null);
        setPayuSessionData(null);
        setPayuPopupBlocked(false);
        setRedirectSeconds(null);
        track('checkout.guest_details_completed', {
            bookHash: prebook.bookHash,
            roomCount: occupancy.length,
            secondsToComplete: checkoutStartedAtRef.current ? Math.round((Date.now() - checkoutStartedAtRef.current) / 1000) : 0,
        });
        try {
            const phoneDigits = phone.replace(/\D/g, '');
            if (typeof __DEV__ !== 'undefined' && __DEV__ && phoneDigits.length >= 10 && phoneDigits.endsWith('7')) {
                setError('This rate is no longer available. Refreshing your rate now.');
                const refreshed = await api.booking.prebook({ hash: params.bookHash });
                if (refreshed.data.status !== 'ok' || !refreshed.data.bookHash) {
                    setUnavailable(true);
                    submittingRef.current = false;
                    setSubmitting(false);
                    return;
                }
                const refreshedAmount = Number(refreshed.data.rate?.payment_options?.payment_types?.[0]?.amount || 0);
                const currentAmount = Number(prebook.rate?.payment_options?.payment_types?.[0]?.amount || 0);
                setPrebook(refreshed.data);
                track('checkout.prebook_result', {
                    bookHash: refreshed.data.bookHash,
                    priceChanged: refreshed.data.priceChanged,
                    oldTotal: { amount: totalWithBookingFee(prebook.rate, nights), currency: currencyOf(prebook.rate) },
                    newTotal: { amount: totalWithBookingFee(refreshed.data.rate, nights), currency: currencyOf(refreshed.data.rate) },
                });
                if (refreshedAmount !== currentAmount || refreshed.data.priceChanged) {
                    setPriceChangeAccepted(false);
                    setError('The price has changed. Review and accept the updated rate before continuing.');
                    submittingRef.current = false;
                    setSubmitting(false);
                    return;
                }
                setError('The same rate is available again. Checkout has been restored; use a different test number to continue to payment.');
                submittingRef.current = false;
                setSubmitting(false);
                return;
            }
            lastStepRef.current = 'payment_redirect';

            const { selectedCurrency, rates } = useCurrency.getState();
            const convertedTotal = convertPriceCeil(total, 'USD', selectedCurrency, rates);

            const paymentPayload: Api.Payment.Initiate.Body = {
                bookHash: prebook.bookHash,
                rateQuote: prebook.rateQuote,
                country: nationality ? nationality.toUpperCase() : 'IN',
                amount: convertedTotal,
                currency: selectedCurrency,
                roomCount: occupancy.length,
                occupancy,
                rooms: guests.map(guest => ({ firstName: normalizeGuestName(guest.first), lastName: normalizeGuestName(guest.last) })),
                contact: { email, phone: fullPhone },
                hotelId: params.hotelId,
                hotelName: params.hotelName,
                thumbnailUrl: extractThumbnailUrl(params.hotelImage),
                hotelAddress: params.hotelAddress,
                hotelStarRating: hotelStars || undefined,
                hotelReviewScore: reviewScore || undefined,
                hotelReviewCount: reviewCount || undefined,
                roomName: prebook.rate?.room_name || params.roomName || 'Selected room',
                checkin: params.checkin,
                checkout: params.checkout,
                freeCancellationBefore,
            };
            stashCheckout({ bookHash: prebook.bookHash, rooms: paymentPayload.rooms, contact: paymentPayload.contact, payment: paymentPayload });
            setOpeningPayment(true);
            setPayuModalVisible(true);
            const res = await api.payment.initiate(paymentPayload);

            if (res.data) {
                const data = res.data;
                // Local mock uses the same status panel, without a PayU window.
                if (data.isMock) {
                    setPayuSessionData(data);
                    setOpeningPayment(false);
                    setSubmitting(false);
                    submittingRef.current = false;
                    return;
                }

                if (data.payu) {
                    setPayuSessionData(data);
                    setSubmitting(false);
                    submittingRef.current = false;
                    return;
                }

                // An unexpected payment response must not fall through to a
                // supplier booking without a verified customer payment.
            }

            if (res.error) {
                if (res.error === 'rate_quote_missing_or_expired') {
                    // The server will not charge against an old or unsigned
                    // supplier quote. Require a fresh rate check and review.
                    setUnavailable(true);
                    setError('Your rate check expired. Check the current price again before payment.');
                    setPayuInitiationError('Your rate check expired. Check the current price again before payment.');
                } else {
                    const message = errorMessage(res, 'Payment could not be started. Please try again.');
                    setError(message);
                    setPayuInitiationError(message);
                }
            } else {
                setError('Payment could not be started. Please try again.');
                setPayuInitiationError('Payment could not be started. Please try again.');
            }
        } catch (err: any) {
            setError(err?.message || 'Payment initiation failed');
            setPayuInitiationError(err?.message || 'Payment initiation failed');
        }
        setOpeningPayment(false);
        submittingRef.current = false;
        setSubmitting(false);
    }

    return (
        <Screen>
            <TopNav />
            <View style={styles.page}>
                <Text style={styles.pageHeading}>Confirm your stay</Text>
                <View style={[styles.columns, narrow && styles.columnsNarrow]}>
                        <View style={[styles.propertyColumn, narrow ? styles.fullColumn : styles.propertySticky]}>
                            {/* Hotel details */}
                            <View style={styles.card}>
                                {hotelPhoto ? <Image source={hotelPhoto} resizeMode="cover" style={styles.hotelHeroImage} /> : <View style={[styles.hotelHeroImage, styles.hotelHeroImageFallback]}><Ionicons name="business-outline" size={28} color={colors.inkFaint} /></View>}
                                <View style={styles.hotelDetailsBody}>
                                    {hotelStars > 0 ? <View style={styles.starsRow}>{Array.from({ length: Math.min(5, hotelStars) }).map((_, index) => <Ionicons key={index} name="star" size={14} color="#D79400" />)}</View> : null}
                                    <Text style={styles.hotelNameLg}>{params.hotelName || 'Your hotel'}</Text>
                                    <Text style={styles.hotelAddressLg}>{params.hotelAddress || 'Hotel address'}</Text>
                                    {reviewScore > 0 ? <View style={styles.reviewRow}><Text style={styles.reviewBadge}>{reviewScore.toFixed(1)}</Text><Text style={styles.reviewText}>{ratingLabel(reviewScore)}{reviewCount ? ` · ${reviewCount.toLocaleString()} review${reviewCount === 1 ? '' : 's'}` : ''}</Text></View> : null}
                                    {hotelAmenities.length > 0 ? (
                                        <View style={styles.hotelAmenityRow}>
                                            {hotelAmenities.map((amenity) => <View key={amenity} style={styles.hotelAmenityItem}><Ionicons name={getAmenityIcon(amenity)} size={14} color={colors.inkSoft} /><Text style={styles.hotelAmenityText} numberOfLines={1}>{amenity}</Text></View>)}
                                        </View>
                                    ) : null}
                                </View>
                            </View>

                            {/* Your booking details */}
                            <View style={[styles.card, styles.cardPadding]}>
                                <Text style={styles.sectionTitle}>Your booking details</Text>
                                <View style={styles.datesRow}>
                                    <View style={styles.dateCol}>
                                        <Text style={styles.dateColLabel}>Check-in</Text>
                                        <Text style={styles.dateColValue}>{dateLabelFull(params.checkin)}</Text>
                                        {params.checkInTime ? <Text style={styles.dateColTime}>{params.checkInTime}</Text> : null}
                                    </View>
                                    <View style={styles.dateDivider} />
                                    <View style={styles.dateCol}>
                                        <Text style={styles.dateColLabel}>Check-out</Text>
                                        <Text style={styles.dateColValue}>{dateLabelFull(params.checkout)}</Text>
                                        {params.checkOutTime ? <Text style={styles.dateColTime}>{params.checkOutTime}</Text> : null}
                                    </View>
                                </View>

                                <View style={styles.divider} />

                                <View style={styles.selectionBlock}>
                                    <Text style={styles.subsectionTitle}>You selected</Text>
                                    <Text style={styles.stayDates}>{nights} night{nights === 1 ? '' : 's'}, {occupancy.length} room{occupancy.length === 1 ? '' : 's'} for {occupancySummaryLabel(occupancy)}</Text>
                                    <Text style={styles.selectedRoomLine}>{occupancy.length} × {roomTitle}</Text>

                                    <Pressable accessibilityRole="button" onPress={() => router.back()}>
                                        <Text style={styles.changeSelectionLink}>Change your selection</Text>
                                    </Pressable>
                                </View>
                            </View>

                            {/* Your price summary */}
                            <View style={[styles.card, styles.cardPadding]}>
                                <View style={styles.priceHeaderRow}>
                                    <Text style={styles.sectionTitle}>Your price summary</Text>
                                    <Text style={styles.timerCompactText}>{loading ? 'Checking current price and availability…' : unavailable ? 'Rate check failed' : 'Price confirmed for checkout'}</Text>
                                </View>
                                {unavailable ? <View style={styles.rateError}><Text style={styles.errorText}>We couldn&apos;t confirm this rate. Your details are still here.</Text><Button label="Try rate check again" variant="secondary" onPress={() => setPrebookAttempt(value => value + 1)} /><Button label="Choose another room" variant="tertiary" onPress={() => router.back()} /></View> : null}
                                {!loading && !unavailable && <View style={styles.priceRows}>
                                    <PriceRow label={`${occupancy.length} room${occupancy.length === 1 ? '' : 's'} × ${nights} night${nights === 1 ? '' : 's'}`} value={money(roomTotal)} />
                                    <PriceRow label="SuiteScanner booking fee" value={money(bookingFee)} />
                                    {nonIncludedTaxes.map((tax, idx) => (
                                        <PriceRow key={idx} label={`${formatTaxName(tax.name)} (payable at property)`} value={<TaxAmount amount={tax.amount} currencyCode={tax.currency_code} textStyle={styles.priceValue} />} />
                                    ))}
                                </View>}
                                {!unavailable && <View style={styles.totalRow}><View><Text style={styles.totalLabel}>Total payable online</Text><Text style={styles.totalMeta}>{nights} night{nights === 1 ? '' : 's'}, {occupancy.length} room{occupancy.length === 1 ? '' : 's'}, {guestsCount} guest{guestsCount === 1 ? '' : 's'}</Text><Text style={styles.totalMeta}>{loading ? 'Payable amount pending rate check' : nonIncludedTaxes.length > 0 ? 'Property-payable charges shown separately above' : 'Online taxes & fees included'}</Text></View><View style={styles.totalValueWrap}><Text style={styles.totalValue}>{loading ? 'Checking…' : money(total)}</Text></View></View>}
                            </View>

                            {/* Cancellation policy */}
                            <View style={[styles.card, styles.cardPadding]}>
                                <Text style={styles.sectionTitle}>Cancellation policy</Text>
                                {loading || unavailable ? <Text style={styles.cancellationDetail}>Available after the rate check.</Text> : <><View style={styles.cancellationHeaderRow}>
                                    <Ionicons name={isFreeCancel ? 'shield-checkmark' : 'close-circle-outline'} size={16} color={isFreeCancel ? colors.green : colors.inkSoft} />
                                    <Text style={[styles.cancellationTitle, isFreeCancel && styles.cancellationTitlePositive]}>{isFreeCancel ? 'Free cancellation' : 'Non-refundable'}</Text>
                                </View>
                                <Text style={styles.cancellationDetail}>{isFreeCancel && freeCancellationBefore ? `Until ${cancellationDeadlineLabel(freeCancellationBefore)}` : 'This booking cannot be cancelled for a refund.'}</Text></>}
                            </View>
                        </View>

                        <View style={[styles.formColumn, narrow && styles.fullColumn]}>
                            <View style={styles.card}>
                                {!isMember ? <View style={styles.signInRow}><View style={styles.signInCopy}><View style={styles.avatar}><Ionicons name="person" size={18} color="#8B5CF6" /></View><Text style={styles.signInText}>Sign in to keep all your stays in one place</Text></View><Pressable accessibilityRole="button" onPress={openMemberPrompt} style={styles.signInButton}><Text style={styles.signInButtonText}>Sign in</Text></Pressable></View> : null}
                                <View style={styles.guestSection}>
                                    <Text style={styles.sectionTitle}>Who&apos;s checking in?</Text>
                                    {occupancy.map((room, index) => <View key={index} style={styles.guestBlock}>{occupancy.length > 1 ? <Text style={styles.roomLabel}>Room {index + 1} · {roomOccupancyLabel(room)}</Text> : null}<View style={[styles.formRow, narrow && styles.formRowNarrow]}><Field value={guests[index].first} onChangeText={value => setGuest(index, 'first', value)} placeholder="First name" label="First name" error={guests[index].first.trim() ? guestErrors[index]?.first ?? undefined : undefined} /><Field value={guests[index].last} onChangeText={value => setGuest(index, 'last', value)} placeholder="Last name" label="Last name" error={guests[index].last.trim() ? guestErrors[index]?.last ?? undefined : undefined} /></View></View>)}

                                    <View style={[styles.formRow, narrow && styles.formRowNarrow]}>
                                        <Field value={email} onChangeText={setEmail} placeholder="Email address" label="Email address" keyboardType="email-address" helper="To receive your booking confirmation." />
                                        {/* fieldAutoHeight's width:'100%' is only safe once the OUTER
                                            row has itself gone to a column (narrow) — phoneRowNarrow can
                                            be true on its own (960–maxContentWidth) while formRow is still
                                            a row, and width:'100%' there claims the whole row and collapses
                                            the email field beside it to 0. */}
                                        <View style={[styles.field, narrow && phoneRowNarrow && styles.fieldAutoHeight]}>
                                            <Text style={styles.inputLabel}>Phone number</Text>
                                            <View style={[styles.phoneRow, phoneRowNarrow && styles.phoneRowStacked]}>
                                                <Select
                                                    value={phoneCountry}
                                                    onChange={value => { setPhoneCountry(value); setPhone(current => formatPhoneDigits(current, value)); }}
                                                    options={PHONE_COUNTRY_OPTIONS}
                                                    displayValue={`+${dialCodeFor(phoneCountry)}`}
                                                    searchable
                                                    flags
                                                    title="Country code"
                                                    style={[styles.phoneCountrySelect, phoneRowNarrow && styles.phoneCountrySelectStacked]}
                                                />
                                                <View style={[styles.inputWrap, styles.phoneDigitsWrap, phoneRowNarrow && styles.phoneDigitsWrapStacked, phoneFocused && styles.inputWrapFocused]}>
                                                    <Ionicons name="call-outline" size={16} color={colors.inkFaint} />
                                                    <TextInput
                                                        value={phone}
                                                        onChangeText={(text) => {
                                                            setPhone(formatPhoneDigits(text, phoneCountry));
                                                        }}
                                                        onFocus={() => setPhoneFocused(true)}
                                                        onBlur={() => setPhoneFocused(false)}
                                                        placeholder="00000 00000"
                                                        placeholderTextColor={colors.inkFaint}
                                                        keyboardType="phone-pad"
                                                        style={styles.input}
                                                    />
                                                </View>
                                            </View>
                                            <Text style={styles.inputHelper}>Enter your number in case the hotel needs to contact you.</Text>
                                        </View>
                                    </View>

                                    <View style={styles.field}>
                                        <Text style={styles.inputLabel}>Nationality</Text>
                                        <View style={[styles.inputWrap, styles.readOnlyField]}>
                                            {nationality ? <CountryFlag code={nationality} size={18} /> : <Ionicons name="globe-outline" size={18} color={colors.inkFaint} />}
                                            <Text style={styles.readOnlyValue}>{nationalityName || 'Not selected'}</Text>
                                            {nationality ? (
                                                <Pressable accessibilityRole="button" accessibilityLabel="Change nationality" onPress={() => router.back()}>
                                                    <Text style={styles.emailSuggestion}>Change</Text>
                                                </Pressable>
                                            ) : null}
                                        </View>
                                        <Text style={styles.inputHelper}>{nationality ? 'Chosen when you searched. To change it, go back and update your search.' : 'Nationality is chosen when you search. Go back and search again to continue.'}</Text>
                                        {!nationality ? <Button label="Start a new search" onPress={() => router.push('/')} /> : null}
                                    </View>
                                </View>
                            </View>

                            <View style={styles.card}>
                                <View style={styles.roomConfirmSection}>
                                    <Text style={styles.subsectionTitle}>Your selected room{occupancy.length === 1 ? '' : 's'}</Text>
                                    {occupancy.map((room, index) => {
                                        const mainGuest = `${guests[index].first} ${guests[index].last}`.trim();
                                        return (
                                            <View key={index} style={styles.roomConfirmBlock}>
                                                {occupancy.length > 1 ? <Text style={styles.roomLabel}>Room {index + 1}</Text> : null}
                                                <Text style={styles.roomConfirmName}>{roomTitle}</Text>
                                                {!loading && !unavailable ? <SummaryFact icon={isFreeCancel ? 'shield-checkmark' : 'close-circle-outline'} label={isFreeCancel ? 'Free cancellation' : 'Non-refundable'} positive={isFreeCancel} /> : null}
                                                {!loading && !unavailable ? <SummaryFact icon="bed-outline" label={bedType || 'Bed type subject to availability'} /> : null}
                                                {!loading && !unavailable ? <SummaryFact icon="restaurant-outline" label={hasBreakfast ? 'Breakfast included' : 'Room only'} positive={hasBreakfast} /> : null}
                                                {!loading && !unavailable && smoking === 'no_smoking' ? <SummaryFact icon="ban-outline" label="No smoking" /> : null}
                                                {!loading && !unavailable && smoking === 'smoking' ? <SummaryFact icon="flame-outline" label="Smoking permitted" /> : null}
                                                {!loading && !unavailable ? <SummaryFact icon={payAtProperty ? 'wallet-outline' : 'card-outline'} label={payAtProperty ? 'Pay at property' : 'Pay online'} /> : null}
                                                <SummaryFact icon="people-outline" label={`Guests: ${guestsSummaryLabel(room)}`} />
                                                {mainGuest ? <SummaryFact icon="person-outline" label={`Main guest: ${mainGuest}`} /> : null}
                                            </View>
                                        );
                                    })}
                                </View>
                            </View>

                            <View style={[styles.card, styles.cardPadding]}><Pressable accessibilityRole="button" accessibilityState={{ expanded: specialRequestOpen }} onPress={() => setSpecialRequestOpen(value => !value)} style={styles.cardHeadingRow}><View style={styles.headingCopy}><Text style={styles.subsectionTitle}>Any special requests?</Text><Text style={styles.helperText}>The property will do its best to arrange them.</Text></View><View style={styles.collapseButton}><Ionicons name={specialRequestOpen ? 'remove' : 'add'} size={18} color={colors.inkSoft} /></View></Pressable>{specialRequestOpen ? <><Text style={styles.requestLabel}>Please write your requests in English</Text><RequestField value={specialRequest} onChangeText={setSpecialRequest} /></> : null}</View>

                            <View style={[styles.card, styles.cardPadding]}>
                                <View style={styles.cardHeadingRow}>
                                    <View style={styles.headingCopy}>
                                        <Text style={styles.sectionTitle}>Proceed to payment</Text>
                                        <Text style={styles.helperText}>You’ll continue to PayU to choose a payment method and complete your payment.</Text>
                                    </View>
                                    <View style={styles.securePill}>
                                        <Ionicons name="shield-checkmark" size={13} color={colors.green} />
                                        <Text style={styles.securePillText}>Secure checkout</Text>
                                    </View>
                                </View>
                                <Text style={styles.helperText}>Available methods include UPI, cards and netbanking. PayU will show the options for your payment.</Text>
                                <View style={styles.trustLogoRow}>
                                    <ExpoImage source={require('../../assets/images/visa_logo.svg')} contentFit="contain" style={styles.trustLogo} accessibilityLabel="Visa" />
                                    <ExpoImage source={require('../../assets/images/mastercard_logo.svg')} contentFit="contain" style={styles.trustLogo} accessibilityLabel="Mastercard" />
                                    <ExpoImage source={require('../../assets/images/american_express_logo.svg')} contentFit="contain" style={styles.trustLogo} accessibilityLabel="American Express" />
                                    <ExpoImage source={require('../../assets/images/UPI_logo.svg')} contentFit="contain" style={styles.trustLogo} accessibilityLabel="UPI" />
                                    <ExpoImage source={require('../../assets/images/payu_logo.svg')} contentFit="contain" style={styles.trustLogo} accessibilityLabel="PayU" />
                                </View>
                            </View>

                            {requiresReview ? (
                                <View style={[styles.card, styles.cardPadding, styles.priceChangeCard]}>
                                    <View style={styles.cardHeadingRow}>
                                        <Ionicons name="warning" size={20} color="#A56400" />
                                        <Text style={styles.priceChangeTitle}>{prebook?.priceChanged ? 'The original price is no longer available' : 'Your rate terms have changed'}</Text>
                                    </View>
                                    <Text style={styles.priceChangeText}>Updated total: {money(total)}. Review this rate and its cancellation terms before payment.</Text>
                                    {taxChanges.length > 0 ? <Text style={styles.priceChangeText}>Payable at the property: {taxChanges.join(', ')}.</Text> : null}
                                    {!priceChangeAccepted ? (
                                        <Pressable accessibilityRole="button" onPress={() => setPriceReviewOpen(true)} style={styles.priceAcceptButton}>
                                            <Text style={styles.priceAcceptButtonText}>Review updated rate</Text>
                                        </Pressable>
                                    ) : (
                                        <View style={styles.priceAcceptedRow}>
                                            <Ionicons name="checkmark-circle" size={16} color={colors.green} />
                                            <Text style={styles.priceAcceptedText}>Updated rate accepted ({money(total)})</Text>
                                        </View>
                                    )}
                                </View>
                            ) : null}

                            <View style={[styles.card, styles.cardPadding]}>
                                {submitting ? <Text style={styles.processing}>Getting your reservation ready…</Text> : null}
                                {error && !submitting ? <Text style={styles.errorText}>{error}</Text> : null}
                                <Text style={styles.terms}>By selecting the button, I agree to the <Text accessibilityRole="link" onPress={() => router.push('/terms')} style={styles.termsLink}>booking terms</Text>.</Text>
                                <Button label={submitting ? 'Getting your reservation ready…' : payuSessionData ? 'Resume payment' : loading ? 'Checking current rate…' : unavailable ? 'Rate unavailable' : 'Continue to payment'} disabled={!canSubmit} onPress={continueToPayment} loading={submitting} style={styles.confirmButton} />
                                {!valid && !loading && !unavailable && !submitting ? <Text accessibilityRole="alert" style={styles.missingText}>To continue, add {missing.length > 1 ? `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}` : missing[0]} above.</Text> : null}
                            </View>
                        </View>
                </View>
            </View>

            <ModalSheet visible={priceReviewOpen} onClose={() => setPriceReviewOpen(false)}>
                <View style={styles.priceReviewSheet}>
                    <View style={styles.cardHeadingRow}>
                        <Ionicons name="warning" size={24} color="#A56400" />
                        <Text style={styles.priceChangeTitle}>{prebook?.priceChanged ? 'The original price is no longer available' : 'Your rate terms have changed'}</Text>
                    </View>
                    <Text style={styles.priceChangeText}>Updated total: {money(total)}. Review the rate and cancellation terms before continuing.</Text>
                    {taxChanges.length > 0 ? <Text style={styles.priceChangeText}>Payable at the property: {taxChanges.join(', ')}.</Text> : null}
                    <Text style={styles.priceChangeText}>{isFreeCancel && freeCancellationBefore ? `Free cancellation until ${cancellationDeadlineLabel(freeCancellationBefore)}` : 'Non-refundable'} · {payAtProperty ? 'Pay at property' : 'Pay online'}</Text>
                    <Button label="Choose another room" variant="secondary" onPress={() => { setPriceReviewOpen(false); router.back(); }} />
                    <Button label={`Continue at ${money(total)}`} onPress={() => { setPriceChangeAccepted(true); setPriceReviewOpen(false); }} />
                </View>
            </ModalSheet>

            <PayUModal
                visible={payuModalVisible}
                onClose={(state) => {
                    if (payuSessionData && state !== 'pending') viewPaymentStatus(payuSessionData);
                    else setPayuModalVisible(false);
                }}
                onViewBooking={() => { if (payuSessionData) viewPaymentStatus(payuSessionData); }}
                onOpenPayment={() => { if (payuSessionData) openPaymentPage(payuSessionData); }}
                onPaymentSuccess={() => { try { payuPopupRef.current?.close(); } catch { /* Already closed. */ } }}
                amount={convertPriceCeil(total, 'USD', useCurrency.getState().selectedCurrency, useCurrency.getState().rates)}
                currency={useCurrency.getState().selectedCurrency}
                // PayU always charges in INR; the server's figure is authoritative once the session exists.
                inrAmount={payuSessionData?.currency === 'INR' && payuSessionData.amount ? Number(payuSessionData.amount) : convertPriceCeil(total, 'USD', 'INR', useCurrency.getState().rates)}
                transactionId={payuSessionData?.transactionId}
                partnerOrderId={payuSessionData?.partnerOrderId}
                hasPaymentPage={Boolean(payuSessionData?.payu)}
                popupBlocked={payuPopupBlocked}
                openingPayment={openingPayment}
                redirectSeconds={redirectSeconds}
                initiationError={payuInitiationError}
            />
        </Screen>
    );
}

function Field(props: { value: string; onChangeText: (value: string) => void; placeholder: string; keyboardType?: 'email-address' | 'phone-pad'; label?: string; helper?: string; error?: string; icon?: keyof typeof Ionicons.glyphMap; suggestion?: { label: string; onPress: () => void } }) {
    const [focused, setFocused] = useState(false);
    return <View style={styles.field}>{props.label ? <Text style={styles.inputLabel}>{props.label}</Text> : null}<View style={[styles.inputWrap, focused && styles.inputWrapFocused, Boolean(props.error) && styles.inputWrapError]}>{props.icon ? <Ionicons name={props.icon} size={16} color={colors.inkFaint} /> : null}<TextInput value={props.value} onChangeText={props.onChangeText} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={props.placeholder} placeholderTextColor={colors.inkFaint} keyboardType={props.keyboardType} style={styles.input} /></View>{props.suggestion ? <Pressable accessibilityRole="button" onPress={props.suggestion.onPress}><Text style={styles.emailSuggestion}>{props.suggestion.label}</Text></Pressable> : null}{props.error ? <Text accessibilityRole="alert" style={styles.inputError}>{props.error}</Text> : props.helper ? <Text style={styles.inputHelper}>{props.helper}</Text> : null}</View>;
}

function RequestField({ value, onChangeText }: { value: string; onChangeText: (value: string) => void }) {
    const [focused, setFocused] = useState(false);
    return <TextInput value={value} onChangeText={onChangeText} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder="Any other requests?" placeholderTextColor={colors.inkFaint} multiline textAlignVertical="top" style={[styles.requestInput, focused && styles.inputWrapFocused]} />;
}

function PriceRow({ label, value }: { label: string; value: ReactNode }) { return <View style={styles.priceRow}><Text style={styles.priceLabel}>{label}</Text>{typeof value === 'string' ? <Text style={styles.priceValue}>{value}</Text> : value}</View>; }
function SummaryFact({ icon, label, positive }: { icon: keyof typeof Ionicons.glyphMap; label: string; positive?: boolean }) { return <View style={styles.factRow}><Ionicons name={icon} size={16} color={positive ? colors.green : colors.inkSoft} /><Text style={[styles.factText, positive && styles.factTextPositive]}>{label}</Text></View>; }

const styles = StyleSheet.create({
    page: { width: '100%', maxWidth: maxContentWidth, alignSelf: 'center', paddingHorizontal: space.card, paddingTop: space.cardLarge, paddingBottom: space.sectionMajor }, pageHeading: { marginBottom: space.card, fontFamily: fontFamily.bold, ...type.pageTitle, color: colors.ink },
    columns: { flexDirection: 'row', alignItems: 'flex-start', gap: space.card }, columnsNarrow: { flexDirection: 'column' }, propertyColumn: { width: 380, flexShrink: 0, minWidth: 0, gap: space.card }, propertySticky: { width: 380, flexShrink: 0, minWidth: 0, gap: space.card, ...({ position: 'sticky', top: space.card } as object) }, formColumn: { flex: 1, minWidth: 0, gap: space.card }, fullColumn: { width: '100%', flexGrow: 0, flexBasis: 'auto' },
    card: { overflow: 'hidden', borderWidth: 1, borderColor: '#DEDAD8', borderRadius: 10, backgroundColor: colors.card }, cardPadding: { padding: space.card, gap: space.internal }, signInRow: { minHeight: 64, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(2), borderBottomWidth: 1, borderBottomColor: '#DEDAD8', paddingHorizontal: space.card, paddingVertical: space.compact }, signInCopy: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: space.compact }, avatar: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 15, backgroundColor: '#F0E8FF' }, signInText: { flex: 1, fontFamily: fontFamily.semibold, ...type.bodyLarge, color: colors.ink }, signInButton: { minHeight: 38, justifyContent: 'center', borderWidth: 1, borderColor: '#A8A29E', borderRadius: 8, paddingHorizontal: space.internal }, signInButtonText: { fontFamily: fontFamily.semibold, fontSize: control.secondaryCta.fontSize, color: colors.ink },
    guestSection: { padding: space.card, gap: space.component }, sectionTitle: { fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink }, subsectionTitle: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink }, headingCopy: { flex: 1, gap: space.related }, helperText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkFaint }, guestBlock: { gap: space.related }, roomLabel: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft }, formRow: { flexDirection: 'row', gap: space.internal }, formRowNarrow: { flexDirection: 'column' }, field: { flex: 1, minWidth: 0, gap: spacing(0.55) },
    // formRowNarrow stacks this alongside the email field in a column, where
    // `flex:1` means "split the row's height evenly" rather than "share its
    // width" — with the phone field's own stacked select+input now taller
    // than email's, that even split cut it short and let its helper text
    // spill into whatever followed. Sizing to its own content instead avoids
    // that regardless of how the two fields' heights compare.
    fieldAutoHeight: { flexGrow: 0, flexBasis: 'auto', width: '100%' }, inputLabel: { fontFamily: fontFamily.medium, ...control.inputLabel, color: colors.inkSoft, marginBottom: space.related }, inputWrap: { minHeight: 52, flexDirection: 'row', alignItems: 'center', gap: spacing(1), borderWidth: 1, borderColor: '#B8B3B0', borderRadius: 8, paddingHorizontal: spacing(1.75), backgroundColor: colors.card }, inputWrapFocused: { borderColor: colors.brand }, inputWrapError: { borderColor: colors.pink }, inputError: { paddingHorizontal: space.compact, fontFamily: fontFamily.medium, ...control.inputHelper, color: colors.pink }, input: { flex: 1, minWidth: 0, paddingVertical: 0, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink, ...({ outlineStyle: 'none' } as object) }, inputHelper: { paddingHorizontal: space.compact, fontFamily: fontFamily.regular, ...control.inputHelper, color: colors.inkFaint }, emailSuggestion: { paddingHorizontal: space.compact, fontFamily: fontFamily.medium, ...type.bodyRegular, color: colors.brand, textDecorationLine: 'underline' },
    readOnlyField: { backgroundColor: colors.cream, borderColor: colors.line }, readOnlyValue: { flex: 1, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink },
    phoneRow: { flexDirection: 'row', gap: spacing(0.75) }, phoneRowStacked: { flexDirection: 'column' }, phoneCountrySelect: { width: 136 }, phoneCountrySelectStacked: { width: '100%' }, phoneDigitsWrap: { flex: 1, minWidth: 0 }, phoneDigitsWrapStacked: { flexGrow: 0, flexBasis: 'auto', width: '100%' },
    cardHeadingRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.internal }, collapseButton: { width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#A8A29E', borderRadius: 18 }, requestLabel: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.ink }, requestInput: { minHeight: 118, borderWidth: 1, borderColor: '#B8B3B0', borderRadius: 8, paddingHorizontal: spacing(1.75), paddingVertical: space.compact, fontFamily: fontFamily.regular, ...control.inputValue, color: colors.ink, ...({ outlineStyle: 'none' } as object) },
    // Payment card
    securePill: { flexDirection: 'row', alignItems: 'center', gap: space.micro, borderRadius: radii.sm, backgroundColor: colors.greenTint, paddingHorizontal: space.compact, paddingVertical: space.micro }, securePillText: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.green },
    trustLogoRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: space.compact, marginTop: space.related },
    trustLogo: { width: 66, height: 30 },
    banner: { borderRadius: radii.sm, backgroundColor: colors.pinkTint, padding: space.internal }, bannerText: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.pink }, processing: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.brand }, errorText: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.pink }, confirmButton: { minHeight: 54, borderRadius: 7, backgroundColor: colors.brand }, missingText: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft, textAlign: 'center' }, terms: { textAlign: 'left', fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft }, termsLink: { textDecorationLine: 'underline', fontFamily: fontFamily.semibold, color: colors.ink },
    // Rate update notice
    priceChangeCard: { borderColor: '#D79400', backgroundColor: '#FFF7DD' }, priceChangeTitle: { flex: 1, fontFamily: fontFamily.bold, fontSize: type.bodyLarge.fontSize, color: '#805000' }, priceChangeText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink }, priceAcceptButton: { alignSelf: 'flex-start', backgroundColor: '#805000', paddingHorizontal: space.internal, paddingVertical: space.compact, borderRadius: radii.sm }, priceAcceptButtonText: { color: colors.white, fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize }, priceAcceptedRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro }, priceAcceptedText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.green }, priceReviewSheet: { padding: space.card, gap: space.internal }, rateError: { gap: space.compact },
    // Hotel details card
    hotelHeroImage: { width: '100%', height: 170, backgroundColor: colors.lineSoft }, hotelHeroImageFallback: { alignItems: 'center', justifyContent: 'center' }, hotelDetailsBody: { paddingHorizontal: space.card, paddingTop: space.internal, paddingBottom: space.card }, starsRow: { flexDirection: 'row', gap: spacing(0.25), marginBottom: space.related }, hotelNameLg: { marginBottom: space.compact, fontFamily: fontFamily.bold, ...type.subsectionTitle, color: colors.ink }, hotelAddressLg: { marginBottom: space.compact, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft }, reviewRow: { flexDirection: 'row', alignItems: 'center', gap: space.related }, reviewBadge: { overflow: 'hidden', borderRadius: 7, backgroundColor: colors.brand, paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.625), fontFamily: fontFamily.semibold, fontSize: control.badgeCompact.fontSize, color: colors.white }, reviewText: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
    hotelAmenityRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginTop: space.related, columnGap: space.related, rowGap: space.micro }, hotelAmenityItem: { flexDirection: 'row', alignItems: 'center', gap: space.micro, minWidth: 0 }, hotelAmenityText: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
    // Booking details card
    datesRow: { flexDirection: 'row', alignItems: 'stretch' }, dateCol: { flex: 1, minWidth: 0, gap: space.micro }, dateDivider: { width: 1, alignSelf: 'stretch', backgroundColor: '#DEDAD8', marginHorizontal: space.internal }, dateColLabel: { fontFamily: fontFamily.medium, ...type.bodyRegular, color: colors.inkSoft }, dateColValue: { fontFamily: fontFamily.bold, ...type.bodyLarge, color: colors.ink }, dateColTime: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    divider: { height: 1, backgroundColor: '#DEDAD8' }, selectionBlock: { gap: space.related }, stayDates: { fontFamily: fontFamily.semibold, ...type.bodyLarge, color: colors.ink }, selectedRoomLine: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink }, changeSelectionLink: { marginTop: space.micro, alignSelf: 'flex-start', fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.brand, textDecorationLine: 'underline' },
    factRow: { flexDirection: 'row', alignItems: 'center', gap: space.related }, factText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink }, factTextPositive: { color: colors.green, fontFamily: fontFamily.medium },
    // Cancellation policy card
    cancellationHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: space.related }, cancellationTitle: { fontFamily: fontFamily.semibold, ...type.bodyLarge, color: colors.ink }, cancellationTitlePositive: { color: colors.green }, cancellationDetail: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
    // Price summary card
    priceHeaderRow: { gap: space.micro }, timerCompact: { flexDirection: 'row', alignItems: 'center', gap: space.micro }, timerCompactText: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    priceRows: { gap: space.compact }, priceRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.internal }, priceLabel: { flex: 1, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink }, priceValue: { fontFamily: fontFamily.medium, fontSize: type.bodyRegular.fontSize, color: colors.ink }, totalRow: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: space.internal, marginTop: space.related }, totalLabel: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink }, totalValueWrap: { alignItems: 'flex-end' }, totalValue: { fontFamily: fontFamily.bold, ...price.bookingTotal, color: colors.ink }, totalMeta: { marginTop: space.micro, fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
    // Selected-room confirmation (right column)
    roomConfirmSection: { paddingHorizontal: space.card, paddingTop: space.card, paddingBottom: space.card, gap: space.component }, roomConfirmBlock: { gap: space.related }, roomConfirmName: { fontFamily: fontFamily.semibold, ...type.bodyLarge, color: colors.ink }, roomMetaText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
});

// Checkout collects personal details and takes payment, so it is members-only.
// The hotel page already asks for sign-in before linking here; this covers a
// pasted/bookmarked checkout URL and a session that expired mid-checkout.
function ConfirmScreen() {
    const isMember = useMember(state => state.isMember);
    const openPrompt = useMember(state => state.openPrompt);

    // Open the same sign-in popup used everywhere else, straight away, rather
    // than making the visitor press a button on a bare page first. If they close
    // it, the page below keeps a button to bring it back.
    useEffect(() => {
        if (!isMember) openPrompt();
    }, [isMember, openPrompt]);

    if (isMember) return <CheckoutScreen />;

    return (
        <Screen>
            <TopNav />
            <View style={{ maxWidth: 480, width: '100%', alignSelf: 'center', padding: space.card, gap: space.component, alignItems: 'center' }}>
                <Text style={{ fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink, textAlign: 'center' }}>Sign in to continue your booking</Text>
                <Text style={{ fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft, textAlign: 'center' }}>SuiteScanner+ is free to join. Sign in with Google to enter your details and complete your reservation.</Text>
                <Button label="Sign in to continue" onPress={openPrompt} />
            </View>
        </Screen>
    );
}

export default ConfirmScreen;
